<script setup>
import { ref } from 'vue';
import { useEventListener } from '@vueuse/core';

const props = defineProps({
  containerWidth: { type: Number, default: 0 },
  modelValue: { type: Number, required: true },
});
const emit = defineEmits(['update:modelValue', 'resizeEnd']);

const MIN_RATIO = 0.25;
const MAX_RATIO = 0.75;

const isResizing = ref(false);
const startX = ref(0);
const startRatio = ref(props.modelValue);

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
const getClientX = e => (e.touches ? e.touches[0].clientX : e.clientX);

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startRatio.value = props.modelValue;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
};

const onResizeMove = event => {
  if (!isResizing.value || !props.containerWidth) return;
  if (event.touches) event.preventDefault();
  const deltaX = getClientX(event) - startX.value;
  const newRatio = clamp(
    startRatio.value + deltaX / props.containerWidth,
    MIN_RATIO,
    MAX_RATIO
  );
  emit('update:modelValue', newRatio);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
  emit('resizeEnd', props.modelValue);
};

useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);
useEventListener(document, 'touchcancel', onResizeEnd);
useEventListener(window, 'blur', onResizeEnd);
</script>

<template>
  <div
    class="group relative w-px shrink-0 cursor-col-resize select-none bg-n-weak hover:bg-n-brand transition-colors"
    :class="{ '!bg-n-brand': isResizing }"
    @mousedown="onResizeStart"
    @touchstart.prevent="onResizeStart"
  >
    <div class="absolute inset-y-0 -left-1.5 -right-1.5" />
  </div>
</template>
